import type { PasswordResetDone, PasswordResetInfo } from '@epc/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { wipeOfflineDataSafe } from '../../lib/offline-wipe';
import { meQuery } from '../../lib/session';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

const linkClass = 'inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4';

/**
 * Новый пароль по ссылке из письма (ДС-19): `/reset-password#<ссылка>`. Часть после «#» браузер на сервер не
 * отправляет, а в API ссылка уходит в теле запроса — в журналы запросов она не попадает. После смены пароля все
 * сеансы входа закрыты — человек входит заново на странице входа (почта подставлена), там же портал предложит вход
 * по Face ID или Touch ID.
 */
export function ResetPasswordPage() {
  const token = useRouterState({ select: (s) => s.location.hash.replace(/^#/, '') });
  const qc = useQueryClient();
  const info = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => api<PasswordResetInfo>('/auth/password-reset/check', { body: { token } }),
    enabled: Boolean(token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [doneFor, setDoneFor] = useState<string | null>(null);

  if (doneFor) {
    return (
      <AuthLayout title="Пароль изменён">
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-md bg-success-soft px-4 py-3 text-[14px] text-ink">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
            <p>
              Новый пароль сохранён. Все прежние сеансы входа завершены: на каждом устройстве войдите заново с
              новым паролем.
            </p>
          </div>
          <Link
            to="/login"
            search={{ email: doneFor }}
            className="inline-flex h-12 items-center justify-center rounded-md bg-ink px-5 text-[15px] font-medium text-on-ink"
          >
            Войти в портал
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (info.isLoading)
    return (
      <AuthLayout title="Новый пароль">
        <Spinner />
      </AuthLayout>
    );
  // Сбой связи или слишком частые запросы — не «ссылка не работает», а повторить
  const gone = info.error instanceof ApiError && (info.error.status === 404 || info.error.status === 400);
  if (info.error && !gone) {
    return (
      <AuthLayout title="Новый пароль">
        <div className="flex flex-col gap-4">
          <ErrorBox error={info.error} />
          <Button variant="secondary" size="lg" loading={info.isFetching} onClick={() => info.refetch()}>
            Повторить
          </Button>
        </div>
      </AuthLayout>
    );
  }
  if (!token || info.error || !info.data) {
    return (
      <AuthLayout title="Новый пароль">
        <EmptyState
          title="Ссылка не работает"
          action={
            <div className="flex flex-wrap items-center gap-x-5">
              <Link to="/forgot-password" className={linkClass}>
                Запросить новую ссылку
              </Link>
              <Link to="/login" className={linkClass}>
                Перейти ко входу
              </Link>
            </div>
          }
        >
          Ссылка уже использована, заменена более новой или истёк её срок. Запросите новую — придёт свежее
          письмо.
        </EmptyState>
      </AuthLayout>
    );
  }
  const reset = info.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setError(null);
    if (password !== repeat) {
      setErrors({ repeat: 'Пароли не совпадают' });
      return;
    }
    setBusy(true);
    try {
      const done = await api<PasswordResetDone>('/auth/password-reset/confirm', {
        body: { token, password },
      });
      // Сеансы закрыты на сервере: прежний вход в этом браузере больше не действует
      qc.setQueryData(meQuery.queryKey, null);
      void wipeOfflineDataSafe();
      qc.removeQueries({ queryKey: ['password-reset', token] });
      setDoneFor(done.email);
    } catch (err) {
      // Ссылка погасла, пока форма была открыта (срок, новая ссылка, уже использована) — показать, что делать
      if (err instanceof ApiError && err.status === 404) {
        await info.refetch();
        return;
      }
      if (err instanceof ApiError) setErrors(err.fieldErrors());
      setError(err instanceof Error ? err : new Error('Не удалось сохранить пароль'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Новый пароль"
      subtitle={
        <>
          Для почты <b className="break-all text-ink">{reset.email}</b>. Ссылка действует до{' '}
          {dateTime(reset.expiresAt)}.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        {/* Почта для менеджера паролей: сохранит новый пароль к нужной учётной записи */}
        <input type="email" name="username" autoComplete="username" value={reset.email} readOnly hidden />
        <Field
          label="Новый пароль"
          htmlFor="password"
          hint="Не короче 10 символов, буквы и цифры"
          error={errors.password}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={!!errors.password}
          />
        </Field>
        <Field label="Новый пароль ещё раз" htmlFor="repeat" error={errors.repeat}>
          <Input
            id="repeat"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            invalid={!!errors.repeat}
          />
        </Field>
        <ErrorBox error={Object.keys(errors).length ? null : error} />
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!password || !repeat}>
          Сохранить пароль
        </Button>
        <p className="text-[13px] text-ink-2">
          После сохранения все сеансы входа завершатся — войдите заново с новым паролем.
        </p>
      </form>
    </AuthLayout>
  );
}
